import {
  LABEL_FIT_HEIGHT_FACTOR,
  LABEL_FIT_WIDTH_FACTOR,
  LABEL_GAP_PX,
} from "../constants";
import type { LayoutBounds, LayoutLabelCandidate } from "../types";
import type { PlaceLayoutLabels } from "./types";

const boundsArea = ({ minX, minY, maxX, maxY }: LayoutBounds) =>
  (maxX - minX) * (maxY - minY);

const overlaps = (a: LayoutBounds, b: LayoutBounds) =>
  a.minX < b.maxX && a.maxX > b.minX && a.minY < b.maxY && a.maxY > b.minY;

// Screen boxes are compared without the pan offset: it moves every box alike, so overlaps do not change with it.
export const placeLayoutLabels: PlaceLayoutLabels = ({
  candidates,
  zoomLevel,
  fontScreenPx,
  pinnedShapeId,
}) => {
  // Biggest shapes first, so a small room's label gives way to a hall's; the pinned one before all.
  const ordered = [...candidates].sort((a, b) => {
    if (a.shapeId === pinnedShapeId) return -1;
    if (b.shapeId === pinnedShapeId) return 1;
    return boundsArea(b.bounds) - boundsArea(a.bounds);
  });

  const takenBoxes: LayoutBounds[] = [];
  const placed: LayoutLabelCandidate[] = [];

  for (const candidate of ordered) {
    const isPinned = candidate.shapeId === pinnedShapeId;
    const badgeWidth = fontScreenPx * candidate.badgeWidthPerFontSize;
    const badgeHeight = fontScreenPx * candidate.badgeHeightPerFontSize;

    if (!isPinned) {
      const shapeWidth =
        (candidate.bounds.maxX - candidate.bounds.minX) * zoomLevel;
      const shapeHeight =
        (candidate.bounds.maxY - candidate.bounds.minY) * zoomLevel;
      const fits =
        shapeWidth >= badgeWidth * LABEL_FIT_WIDTH_FACTOR &&
        shapeHeight >= badgeHeight * LABEL_FIT_HEIGHT_FACTOR;
      if (!fits) continue;
    }

    const centreX = candidate.anchor.x * zoomLevel;
    const centreY = candidate.anchor.y * zoomLevel;
    const box: LayoutBounds = {
      minX: centreX - badgeWidth / 2 - LABEL_GAP_PX,
      minY: centreY - badgeHeight / 2 - LABEL_GAP_PX,
      maxX: centreX + badgeWidth / 2 + LABEL_GAP_PX,
      maxY: centreY + badgeHeight / 2 + LABEL_GAP_PX,
    };
    if (!isPinned && takenBoxes.some((taken) => overlaps(taken, box))) continue;

    takenBoxes.push(box);
    placed.push(candidate);
  }

  return placed;
};
